import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * AnimatedBackground (Three.js / WebGL upgrade).
 * Renders a 3D field of glowing particles + subtle wave deformation.
 * Light-theme friendly (low-opacity violet/cyan/pink dots).
 * Progressive enhancement: disabled on mobile + prefers-reduced-motion.
 */
export default function AnimatedBackground() {
    const mountRef = useRef(null);

    useEffect(() => {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        if (reduceMotion || isMobile) return;

        const mount = mountRef.current;
        if (!mount) return;

        const w = window.innerWidth;
        const h = window.innerHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 1000);
        camera.position.z = 300;

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        // Particle field
        const COUNT = 1100;
        const positions = new Float32Array(COUNT * 3);
        const colors = new Float32Array(COUNT * 3);
        const sizes = new Float32Array(COUNT);

        const palette = [
            new THREE.Color("#7c3aed"),
            new THREE.Color("#a855f7"),
            new THREE.Color("#06b6d4"),
            new THREE.Color("#3b82f6"),
            new THREE.Color("#ec4899"),
        ];

        for (let i = 0; i < COUNT; i++) {
            positions[i * 3 + 0] = (Math.random() - 0.5) * 800;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 500;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 600;

            const c = palette[Math.floor(Math.random() * palette.length)];
            colors[i * 3 + 0] = c.r;
            colors[i * 3 + 1] = c.g;
            colors[i * 3 + 2] = c.b;

            sizes[i] = Math.random() * 3 + 1;
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
        geo.setAttribute("size", new THREE.BufferAttribute(sizes, 1));

        // Glow sprite texture (radial gradient)
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = 64;
        const ctx = canvas.getContext("2d");
        const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
        grad.addColorStop(0, "rgba(255,255,255,1)");
        grad.addColorStop(0.4, "rgba(255,255,255,0.6)");
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 64, 64);
        const sprite = new THREE.CanvasTexture(canvas);

        const mat = new THREE.PointsMaterial({
            size: 4,
            map: sprite,
            vertexColors: true,
            transparent: true,
            opacity: 0.55,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            sizeAttenuation: true,
        });

        const points = new THREE.Points(geo, mat);
        scene.add(points);

        // Mouse
        const mouse = { x: 0, y: 0 };
        const onMove = (e) => {
            mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
            mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
        };
        window.addEventListener("mousemove", onMove);

        // Resize
        const onResize = () => {
            const W = window.innerWidth;
            const H = window.innerHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener("resize", onResize);

        let raf;
        const t0 = performance.now();
        const animate = () => {
            const t = (performance.now() - t0) * 0.0005;

            // Rotate the entire field slowly
            points.rotation.y = t * 0.4 + mouse.x * 0.2;
            points.rotation.x = -mouse.y * 0.15;

            // Wave deformation on Y
            const pos = geo.attributes.position.array;
            for (let i = 0; i < COUNT; i++) {
                const ix = i * 3;
                pos[ix + 1] += Math.sin(t * 2 + pos[ix] * 0.01) * 0.05;
            }
            geo.attributes.position.needsUpdate = true;

            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };
        animate();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("resize", onResize);
            geo.dispose();
            mat.dispose();
            sprite.dispose();
            renderer.dispose();
            if (renderer.domElement.parentNode) {
                renderer.domElement.parentNode.removeChild(renderer.domElement);
            }
        };
    }, []);

    return (
        <>
            <div ref={mountRef} className="aura-particles" aria-hidden="true" />
            {/* Decorative aurora blobs (CSS, kept for color depth) */}
            <div
                className="aurora-blob animate-pulse-glow"
                style={{
                    width: 420,
                    height: 420,
                    top: "-120px",
                    left: "-100px",
                    background: "radial-gradient(circle, rgba(124,58,237,0.45), transparent 60%)",
                }}
            />
            <div
                className="aurora-blob animate-pulse-glow"
                style={{
                    width: 520,
                    height: 520,
                    top: "30%",
                    right: "-180px",
                    background: "radial-gradient(circle, rgba(6,182,212,0.35), transparent 65%)",
                    animationDelay: "1.5s",
                }}
            />
            <div
                className="aurora-blob animate-pulse-glow"
                style={{
                    width: 380,
                    height: 380,
                    bottom: "-160px",
                    left: "35%",
                    background: "radial-gradient(circle, rgba(236,72,153,0.25), transparent 60%)",
                    animationDelay: "3s",
                }}
            />
        </>
    );
}

